import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZTextarea } from "@/components/ZInput";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { formatEventDate, formatAmount } from "@/lib/format";
import { CheckCircle2, XCircle, Calendar, MapPin, ShieldCheck } from "lucide-react";

const KIND_LABELS = {
  events: { label: "Événement", icon: "🎟️" },
  fundraisers: { label: "Cagnotte", icon: "💚" },
  votes: { label: "Vote", icon: "🗳️" },
  surveys: { label: "Sondage", icon: "📋" },
};

const TABS = [
  { id: "pending", label: "En attente" },
  { id: "rejected", label: "Rejetés" },
  { id: "published", label: "Publiés" },
];

export default function AdminModerationPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/moderation", { params: { status_filter: tab } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const approve = async (it) => {
    setErr("");
    setBusy(true);
    try {
      await api.post(`/admin/moderation/${it.kind}/${it.id}/approve`);
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!reason.trim()) return setErr("Indiquez un motif.");
    setErr("");
    setBusy(true);
    try {
      await api.post(`/admin/moderation/${reject.kind}/${reject.id}/reject`, { reason: reason.trim() });
      setReject(null); setReason("");
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="z-container" data-testid="admin-moderation-page">
      <h1 className="z-page-title">Modération</h1>
      <p className="z-page-sub">Validez ou rejetez les éléments publiés par les organisateurs.</p>

      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`z-chip ${tab === t.id ? "z-chip-active" : ""}`} data-testid={`mod-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {err && <p className="z-error-text">{err}</p>}

      {loading ? (
        <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={28} />}
          title={tab === "pending" ? "Aucun élément en attente" : `Aucun élément ${tab === "rejected" ? "rejeté" : "publié"}`}
          message="La file est vide pour le moment."
          testId="moderation-empty"
        />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {items.map((it) => (
            <div key={`${it.kind}-${it.id}`} className="z-card" style={{ padding: 16 }} data-testid={`mod-item-${it.id}`}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                {it.kind === "events" && it.image_url && (
                  <img src={it.image_url} alt="" style={{ width: 90, height: 90, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                    <span className="z-badge z-badge-teal">{KIND_LABELS[it.kind].icon} {KIND_LABELS[it.kind].label}</span>
                    {it.is_private && <span className="z-badge">Privé</span>}
                    {it.kind === "events" && it.price === 0 && <span className="z-badge z-badge-success">Gratuit</span>}
                  </div>
                  <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700 }}>{it.title}</h3>
                  <div style={{ fontSize: 13, color: "var(--z-muted)" }}>par <strong>{it.organizer_name}</strong></div>
                  {it.kind === "events" && (
                    <div style={{ fontSize: 13, color: "var(--z-muted)", marginTop: 4 }}>
                      <Calendar size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
                      {formatEventDate(it.starts_at)}
                      <span style={{ margin: "0 6px" }}>·</span>
                      <MapPin size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
                      {it.location}
                      <span style={{ margin: "0 6px" }}>·</span>
                      {it.price > 0 ? formatAmount(it.price, it.currency) : "Gratuit"} · cap. {it.capacity}
                    </div>
                  )}
                  {it.kind === "fundraisers" && (
                    <div style={{ fontSize: 13, color: "var(--z-muted)", marginTop: 4 }}>Objectif : {formatAmount(it.goal, "GNF")}</div>
                  )}
                  {it.description && <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--z-text)", lineHeight: 1.5 }}>{it.description}</p>}
                  {it.reject_reason && (
                    <div style={{ marginTop: 8, fontSize: 13, color: "var(--z-error)", background: "rgba(161,44,123,0.08)", padding: 8, borderRadius: 8 }}>
                      Motif du rejet : {it.reject_reason}
                    </div>
                  )}
                </div>
              </div>
              {tab === "pending" && (
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <ZButton variant="teal" onClick={() => approve(it)} loading={busy} data-testid={`mod-approve-${it.id}`}>
                    <CheckCircle2 size={16} /> Publier
                  </ZButton>
                  <ZButton variant="outline" onClick={() => { setReject(it); setReason(""); }} data-testid={`mod-reject-${it.id}`}>
                    <XCircle size={16} /> Rejeter
                  </ZButton>
                </div>
              )}
              {tab === "rejected" && (
                <div style={{ marginTop: 10 }}>
                  <ZButton variant="teal" size="sm" onClick={() => approve(it)} loading={busy} data-testid={`mod-republish-${it.id}`}>
                    Republier
                  </ZButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={!!reject} onClose={() => setReject(null)} title="Motif du rejet" testId="reject-modal">
        <p style={{ fontSize: 14, color: "var(--z-muted)", marginBottom: 10 }}>
          Expliquez à l'organisateur pourquoi cet élément est rejeté.
        </p>
        <ZTextarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Ex: photo de couverture inappropriée, infos incomplètes…"
          required data-testid="reject-reason-input"
        />
        <ZButton variant="danger" block onClick={doReject} loading={busy} data-testid="reject-confirm-btn">
          Rejeter
        </ZButton>
      </Modal>
    </div>
  );
}
